import type { HTMLAttributes, ReactElement } from 'react'
import { cn } from '@/lib/cn'

export type BadgeVariant = 'neutral' | 'accent' | 'success' | 'warning' | 'danger' | 'info'

export interface BadgeProps extends HTMLAttributes<HTMLSpanElement> {
  variant?: BadgeVariant
  size?: 'sm' | 'md'
}

const variants: Record<BadgeVariant, string> = {
  neutral: 'border-line bg-sunken text-fg-muted',
  accent: 'border-brand-500/25 bg-accent-soft text-brand-700 dark:text-brand-200',
  success: 'border-ok-500/25 bg-ok-100 text-ok-700 dark:bg-ok-950 dark:text-ok-300',
  warning: 'border-warn-500/25 bg-warn-100 text-warn-700 dark:bg-warn-950 dark:text-warn-300',
  danger: 'border-bad-500/25 bg-bad-100 text-bad-700 dark:bg-bad-950 dark:text-bad-300',
  info: 'border-info-500/25 bg-info-100 text-info-700 dark:bg-info-950 dark:text-info-300'
}

const sizes: Record<NonNullable<BadgeProps['size']>, string> = {
  sm: 'h-5 gap-1 px-1.5 text-[0.6875rem] [&_svg]:size-3',
  md: 'h-6 gap-1.5 px-2.5 text-xs [&_svg]:size-3.5'
}

export function Badge({
  variant = 'neutral',
  size = 'md',
  className,
  children,
  ...rest
}: BadgeProps): ReactElement {
  return (
    <span
      className={cn(
        'inline-flex shrink-0 items-center whitespace-nowrap rounded-full border font-medium',
        variants[variant],
        sizes[size],
        className
      )}
      {...rest}
    >
      {children}
    </span>
  )
}
